import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { PublicSetUpPaymentsCTA } from './PublicSetUpPaymentsCTA';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import styled from 'foundations-theming/utils';
import { breakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { PageSection, SectionHeader, SectionSubTitle, SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useScopedTranslations } from 'pricing-pages-ui-components/i18n/useScopedTranslations';
import { Fragment, useContext } from 'react';
import { PublicI18nContext } from '../../context/I18nContext';
import { useCurrency } from '../../context/CurrencyContext';
import { AvailableWithSection, FeeDetails, PaymentMethods, ProcessorLogo, PromoSection, ValueProps } from './PublicPaymentProcessorSectionElements';
import { ELIGIBLE_PROCESSORS } from './consts';
import { TRACKING_SOURCES } from '../../utils/tracking';
import processorTranslations from 'i2l?query=sporks!../../../lang/commerce-hub-processors/en.lyaml';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LEARN_MORE_URL = 'https://www.hubspot.com/products/revenue/payments';
const TOTAL_ROWS = 8;
const StyledDiv = styled.div.withConfig({
  displayName: "PublicPaymentsProcessorsSection__StyledDiv"
})(({
  columns,
  theme
}) => {
  return {
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    flexGrow: 1,
    maxInlineSize: '100%',
    [breakpoint.tablet]: {
      gridTemplateColumns: '1fr',
      padding: 0,
      '& > *': {
        padding: `${theme.space['400']} !important`
      }
    }
  };
});
const GridStart = styled.div.withConfig({
  displayName: "PublicPaymentsProcessorsSection__GridStart"
})(({
  row,
  column,
  theme,
  span
}) => {
  return {
    gridRowStart: row,
    gridColumnStart: column,
    gridColumnEnd: column + (span !== null && span !== void 0 ? span : 1),
    [breakpoint.tablet]: {
      marginTop: row === 2 && column > 1 ? theme.space['1300'] : 0,
      gridRowStart: column > 1 ? (column - 1) * TOTAL_ROWS + row : row,
      gridColumnStart: 1,
      gridColumnEnd: -1,
      inlineSize: '100%'
    }
  };
});
const CardSectionContent = styled.div.withConfig({
  displayName: "PublicPaymentsProcessorsSection__CardSectionContent"
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  flexGrow: 1,
  inlineSize: '100%',
  borderBlockStartWidth: '1px',
  borderBlockStartStyle: 'solid',
  borderBlockStartColor: 'var(--border-core-subtle)',
  marginBlockStart: theme.space['400'],
  paddingBlockStart: theme.space['400'],
  paddingInline: theme.space['600'],
  [breakpoint.tablet]: {
    paddingInline: theme.space['200']
  }
}));
const CardSection = ({
  children
}) => /*#__PURE__*/_jsx(CardSectionContent, {
  children: children
});
export const PublicPaymentsProcessorsSection = ({
  onLearnMoreClick,
  screen
}) => {
  const {
    i18nLoader
  } = useContext(PublicI18nContext);
  const T = useScopedTranslations(processorTranslations, i18nLoader);
  const {
    countryCode
  } = useCurrency();
  const sortedProcessors = [...ELIGIBLE_PROCESSORS].sort(a => a === 'HS_PAYMENTS' ? -1 : 1);
  return /*#__PURE__*/_jsx(PageSection, {
    children: /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      children: /*#__PURE__*/_jsx(StyledDiv, {
        columns: sortedProcessors.length,
        children: /*#__PURE__*/_jsxs(CardWrapper, {
          display: "grid",
          inlineSize: "100%",
          columnGap: "800",
          padding: "1300",
          marginBlockEnd: "0",
          children: [/*#__PURE__*/_jsx(GridStart, {
            row: 1,
            column: 1,
            span: sortedProcessors.length,
            children: /*#__PURE__*/_jsx(SectionHeader, {
              children: /*#__PURE__*/_jsxs(T.Skeleton, {
                widths: ['70%', '90%'],
                heights: [28, 16],
                children: [/*#__PURE__*/_jsx(SectionTitle, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "commerce-hub.processorCards.title"
                  })
                }), /*#__PURE__*/_jsx(SectionSubTitle, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "commerce-hub.processorCards.subtitle"
                  })
                })]
              })
            })
          }), sortedProcessors.map((processor, index) => /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(GridStart, {
              row: 2,
              column: index + 1,
              children: /*#__PURE__*/_jsx(ProcessorLogo, {
                processor: processor
              })
            }), /*#__PURE__*/_jsx(GridStart, {
              row: 3,
              column: index + 1,
              children: /*#__PURE__*/_jsx(T.Skeleton, {
                widths: ['80%'],
                heights: [14],
                children: /*#__PURE__*/_jsx(PromoSection, {
                  processor: processor
                })
              })
            }), /*#__PURE__*/_jsx(GridStart, {
              row: 4,
              column: index + 1,
              children: /*#__PURE__*/_jsx(CardSection, {
                children: /*#__PURE__*/_jsx(T.Skeleton, {
                  widths: ['90%', '90%', '90%', '90%', '90%'],
                  heights: [16, 16, 16, 16, 16],
                  children: /*#__PURE__*/_jsx(ValueProps, {
                    processor: processor
                  })
                })
              })
            }), /*#__PURE__*/_jsx(GridStart, {
              row: 5,
              column: index + 1,
              children: /*#__PURE__*/_jsx(CardSection, {
                children: /*#__PURE__*/_jsx(T.Skeleton, {
                  widths: ['80%', '60%'],
                  heights: [16, 16],
                  children: /*#__PURE__*/_jsx(PaymentMethods, {
                    processor: processor,
                    countryCode: countryCode
                  })
                })
              })
            }), /*#__PURE__*/_jsx(GridStart, {
              row: 6,
              column: index + 1,
              children: /*#__PURE__*/_jsx(CardSection, {
                children: /*#__PURE__*/_jsx(T.Skeleton, {
                  widths: ['80%', '90%', '60%'],
                  heights: [16, 16, 16],
                  children: /*#__PURE__*/_jsx(FeeDetails, {
                    processor: processor,
                    countryCode: countryCode
                  })
                })
              })
            }), /*#__PURE__*/_jsx(GridStart, {
              row: 7,
              column: index + 1,
              children: /*#__PURE__*/_jsx(CardSection, {
                children: /*#__PURE__*/_jsx(T.Skeleton, {
                  widths: ['90%'],
                  heights: [16],
                  children: /*#__PURE__*/_jsx(AvailableWithSection, {
                    processor: processor,
                    countryCode: countryCode
                  })
                })
              })
            })]
          }, processor)), /*#__PURE__*/_jsx(GridStart, {
            row: 8,
            column: 1,
            span: sortedProcessors.length,
            children: /*#__PURE__*/_jsx(Flex, {
              justifyContent: "center",
              alignItems: "center",
              gap: "300",
              marginBlockStart: "1000",
              flexWrap: "wrap",
              children: /*#__PURE__*/_jsxs(T.Skeleton, {
                widths: ['160px', '120px'],
                heights: [36, 36],
                children: [/*#__PURE__*/_jsx(PublicSetUpPaymentsCTA, {
                  use: "primary",
                  screen: screen,
                  trackingSource: TRACKING_SOURCES.PAYMENTS_PROCESSORS_SECTION
                }), /*#__PURE__*/_jsx(Button, {
                  use: "secondary",
                  href: LEARN_MORE_URL,
                  external: true,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  onClick: onLearnMoreClick,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "commerce-hub.processorCards.learnMoreButton"
                  })
                })]
              })
            })
          })]
        })
      })
    })
  });
};